import { ImagePreset, ImageSizes, ImgProxyParams } from '@emico-react/image'
import getImgProxyUrl from '@emico-react/image/src/getImgProxyUrl'
import { HTMLAttributes } from 'react'
import { Helmet } from 'react-helmet'

import { breakpointToPixels } from '@emico/styles'
import { last } from '@emico/utils'

export interface SizesSourceProps extends HTMLAttributes<HTMLSourceElement> {
    /** URL of the image */
    url: string

    /** Width of the image, used when generating high resolution image variants. Optional but recommended. */
    width?: number

    /** Height of the image, used when generating high resolution image variants. Optional but recommended. */
    height?: number

    /** Optional property for ratio calculations when only providing one of width or height */
    originalHeight?: number

    /** Optional property for ratio calculations when only providing one of width or height */
    originalWidth?: number

    /**
     * Image sizes can be defined using devices (mobile, tablet, desktop),
     * breakpoints defined in layout.ts e.g. xs, sm, md, lg, xl or using
     * pixels. Using `{ '1500': ... }` as a breakpoint will add a
     * `(min-width: 1500px)` size breakpoint.
     * The value you define will be the pixel size of the image, you can
     * set this to `auto` if you want to keep this in sync with the breakpoints.
     */
    sizes: ImageSizes

    /** Extra imaginary configuration when generated resized images. */
    imngProxyParams?: Partial<ImgProxyParams>

    /** Get width, height and sizes from a preset */
    preset?: ImagePreset

    media: string
}

export function HighPrioPictureSource({
    url,
    width,
    height,
    originalHeight,
    originalWidth,
    sizes: sizesParam,
    imngProxyParams,
    preset,
    media,
    ...props
}: SizesSourceProps) {
    const sizes = sizesParam ?? preset?.sizes

    const breakpoints = Object.entries(sizes).map(([breakpoint, width]) => {
        const pixels = breakpointToPixels(breakpoint)
        const realWidth = width === 'auto' ? last(pixels) : width
        const sizeUrl = getImgProxyUrl(url, {
            width: realWidth,
            ...imngProxyParams,
        })

        return { breakpoint: pixels[0], width: realWidth, url: sizeUrl }
    })

    // Sort breakpoints from large to small
    breakpoints.sort((a, b) => b.breakpoint - a.breakpoint)

    /**
     * Generate `sizes` and `srcSet` attributes:
     * sizes:  [...(min-width: {breakpoint}px) {width}px], {width}px
     * srcSet: [...{url} {width}w]
     */
    const fallback = last(breakpoints)
    const sizesAttr = breakpoints
        .slice(0, -1) // The last breakpoint will also be the fallback
        .map((o) => `(min-width: ${o.breakpoint}px) ${o.width}px`)

    sizesAttr.push(`${fallback.width}px`)

    const srcSet = breakpoints.map((o) => `${o.url} ${o.width}w`).join(', ')

    return (
        <>
            <Helmet>
                <link
                    rel="preload"
                    href={fallback.url}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    imageSrcSet={srcSet}
                    imageSizes={sizesAttr.join(', ')}
                    as="image"
                    media={media}
                />
            </Helmet>
            <source
                srcSet={srcSet}
                src={fallback.url}
                sizes={sizesAttr.join(', ')}
                // width={width}
                // height={height}
                media={media}
                {...props}
            />
        </>
    )
}
